import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ChatListFilters, ChatListTable, NewTestChatModal } from '@/components/chat';
import { useChatThreads } from '@/hooks/useChatThreads';
import type { ChatFilters } from '@/types/chat';

const DEFAULT_PAGE_SIZE = 20;

export function ChatPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<ChatFilters>({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [testChatModalOpen, setTestChatModalOpen] = useState(false);

  const { data, isLoading, error: fetchError } = useChatThreads(filters);
  const hasError = !!fetchError;

  function handleFiltersChange(newFilters: ChatFilters) {
    setFilters({ ...newFilters, page: 1, pageSize: DEFAULT_PAGE_SIZE });
  }

  function handleSelect(threadId: string) {
    navigate(`/chat/${threadId}`);
  }

  function handlePrev() {
    setFilters((prev) => ({ ...prev, page: Math.max(1, (prev.page ?? 1) - 1) }));
  }

  function handleNext() {
    setFilters((prev) => ({ ...prev, page: (prev.page ?? 1) + 1 }));
  }

  const threads = data?.threads ?? [];
  const total = data?.total ?? 0;
  const currentPage = filters.page ?? 1;
  const pageSize = filters.pageSize ?? DEFAULT_PAGE_SIZE;
  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Conversas</h1>
          <p className="text-muted-foreground">
            Visualize e inspecione o fluxo de mensagens do agente
          </p>
        </div>

        <Button onClick={() => setTestChatModalOpen(true)}>
          + Novo Chat de Teste
        </Button>
      </div>

      {/* Filters */}
      <ChatListFilters filters={filters} onChange={handleFiltersChange} />

      {/* Error state */}
      {hasError && (
        <div className="flex items-center gap-2 text-destructive py-4">
          <AlertCircle className="h-5 w-5" />
          <span className="text-sm">Erro ao carregar conversas. Tente novamente.</span>
        </div>
      )}

      {/* Loading state (first load without existing data) */}
      {isLoading && !data && (
        <div className="flex justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Table */}
      {!hasError && (
        <ChatListTable
          threads={threads}
          onSelect={handleSelect}
          isLoading={isLoading && !data}
        />
      )}

      {/* Pagination */}
      {!hasError && total > pageSize && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Mostrando {Math.min((currentPage - 1) * pageSize + 1, total)}–
            {Math.min(currentPage * pageSize, total)} de {total}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrev}
              disabled={currentPage <= 1 || isLoading}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNext}
              disabled={currentPage >= totalPages || isLoading}
            >
              Próxima
            </Button>
          </div>
        </div>
      )}
      <NewTestChatModal
        open={testChatModalOpen}
        onClose={() => setTestChatModalOpen(false)}
        onSuccess={(threadId) => navigate('/chat/' + threadId)}
      />
    </div>
  );
}
